import React, { PureComponent } from 'react';
import { injectIntl } from 'react-intl-next';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from 'styled-components';
import AkButtonGroup from '@atlaskit/button/button-group';
import AkButton from '@atlaskit/button/standard-button';
import ErrorIcon from '@atlaskit/icon/core/error';
import Spinner from '@atlaskit/spinner';
import { R400, N200 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';

import { CREATE_ERROR, KEY_ERROR, NO_ERROR } from '../../../state/ui/create-space/types';
import { fireUIAnalytics } from '@atlassian/analytics-bridge';

import messages from './messages';
import { type Props, type DefaultProps } from './types';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	display: 'flex',
	justifyContent: 'flex-end',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const WrappedErrorIcon = styled(ErrorIcon)({
	marginRight: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ConnectionFailed = styled.div({
	alignItems: 'center',
	color: token('color.text.subtlest', N200),
	display: 'flex',
	font: token('font.body.UNSAFE_small'),
});

// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage, @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpinnerDiv = styled.div({
	display: 'inline-block',
	marginRight: token('space.100', '8px'),
	position: 'relative',
	top: token('space.075', '6px'),
});

class Footer extends PureComponent<Props> {
	static defaultProps: DefaultProps = {
		errorState: NO_ERROR,
	};

	renderConnectionError = (onRetry: any) => {
		const {
			intl: { formatMessage },
		} = this.props;
		return (
			<ConnectionFailed>
				<WrappedErrorIcon
					label={formatMessage(messages.error)}
					color={token('color.icon.danger', R400)}
				/>
				{formatMessage(messages.connectionError)}
				<AkButton
					// TODO: (from codemod) "link" and "subtle-link" appearances are only available in LinkButton, please either provide a href prop then migrate to LinkButton, or remove the appearance from the default button.
					appearance="link"
					onClick={(event, analyticsEvent) => {
						onRetry(analyticsEvent);
					}}
				>
					{formatMessage(messages.retry)}
				</AkButton>
			</ConnectionFailed>
		);
	};

	render() {
		const {
			intl: { formatMessage },
		} = this.props;
		const {
			isCreatingSpace,
			isGeneratingKey,
			errorState,
			suggestedKey,
			onSpaceNameChanged,
			onCreate,
			onCancel,
			userEnteredSpaceName,
			userEnteredSpaceNameInvalid,
		} = this.props;
		return (
			<Container>
				{errorState === KEY_ERROR &&
					this.renderConnectionError((analyticsEvent: any) => {
						onSpaceNameChanged(userEnteredSpaceName);
						fireUIAnalytics(analyticsEvent, 'retrySpaceNameChangeButton');
					})}
				{errorState === CREATE_ERROR &&
					this.renderConnectionError((analyticsEvent: any) => {
						onCreate();
						fireUIAnalytics(analyticsEvent, 'retrySpaceNameChangeButton');
					})}
				{(isCreatingSpace || isGeneratingKey) && (
					<SpinnerDiv>
						<Spinner size="small" />
					</SpinnerDiv>
				)}
				<AkButtonGroup>
					<AkButton
						appearance="link"
						isDisabled={isCreatingSpace}
						onClick={(_, analyticsEvent) => {
							fireUIAnalytics(analyticsEvent, 'cancelButton');
							onCancel();
						}}
					>
						{formatMessage(messages.cancel)}
					</AkButton>
					<AkButton
						appearance="primary"
						isDisabled={
							userEnteredSpaceNameInvalid || !suggestedKey || isCreatingSpace || isGeneratingKey
						}
						onClick={(_, analyticsEvent) => {
							fireUIAnalytics(analyticsEvent, 'createButton');
							onCreate();
						}}
						data-test-id="confluence-project-pages.post-expand.create-space-modal.create-button"
					>
						{formatMessage(messages.createSpace)}
					</AkButton>
				</AkButtonGroup>
			</Container>
		);
	}
}

export default injectIntl(Footer);
